import { apm } from "@elastic/apm-rum";
import { FileDownloadOutlined, ZoomOutMap } from "@mui/icons-material";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import html2canvas from "html2canvas";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as TooltipRechart,
  XAxis,
  YAxis,
} from "recharts";

import ClientConfig from "shared/config";
import profileToolImage from "shared/assets/profileToolImage.png";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import {
  calculateSlope,
  Coordinates2d,
  downloadFile,
  getApiKeyHeaders,
} from "shared/common/utils/general-utils";

interface Datas {
  Length: number;
  "Smoothed Height": number;
  "Original Height": number;
  east?: number;
  north?: number;
}

export default function ProfileContent() {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const { t } = useTranslation();

  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );
  const changeShowDescription = sidebarStore(
    (state) => state.changeShowDescription
  );
  const profileToolValues = sidebarStore((state) => state.profileToolValues);
  const profileTool = sidebarStore((state) => state.profileTool);

  const defaultDatas: Datas = {
    Length: 0,
    "Smoothed Height": 0,
    "Original Height": 0,
  };

  const [datas, setDatas] = useState<Datas[]>([defaultDatas]);
  const [smoothLevel, setSmoothLevel] = useState<number>(1);
  const [superelevation, setSuperelevation] = useState<number>(0.2);
  const [edgePreserving, setEdgePreserving] = useState<boolean>(false);
  const [filterPeaks, setFilterPeaks] = useState<boolean>(true);
  const [left, setLeft] = useState<number>(0);
  const [leftZoomed, setLeftZoomed] = useState<number>(0);
  const [right, setRight] = useState<number>(0);
  const [rightZoomed, setRightZoomed] = useState<number>(0);
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);
  const [top, setTop] = useState<number>(0);
  const [bottom, setBottom] = useState<number>(0);
  const [zoomedIn, setzoomedIn] = useState<boolean>(false);

  const [showDownloadSubMenu, setShowDownloadSubMenu] =
    useState<boolean>(false);

  const [slopeCoordsStart, setSlopeCoordsStart] = useState<Coordinates2d>();
  const [slopeCoordsEnd, setSlopeCoordsEnd] = useState<Coordinates2d>();
  const [slopeCoordsVertical, setSlopeCoordsVertical] =
    useState<Array<Coordinates2d>>();
  const [slopeCoordsHorizontal, setSlopeCoordsHorizontal] =
    useState<Array<Coordinates2d>>();
  const [slopeAngle, setSlopeAngle] = useState<number>(0);

  const [hiddenLines, setHiddenLines] = useState<Array<string>>([]);

  const captureRef = useRef(null);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const aspect_ratio_chart = () => {
    if (!maximizeTable) {
      return 1.45;
    } else {
      if (isLgUp) return 2.4; // > lg
      if (isMdUp) return 1.8; // md - lg
      return 1.7; // < md
    }
  };

  function generateChartTicksInterval(min: number, max: number) {
    // Search the next lower interval of the array below. The goal is to display at least 8 ticks: (max - min) / 8
    const array_of_intervals = [10, 5, 2.5, 2, 1, 0.5, 0.25, 0.2, 0.1, 0.05];
    const filtered_array = array_of_intervals.filter(
      (value) => value <= (max - min) / 8
    );
    const interval =
      filtered_array.length > 0 ? Math.max(...filtered_array) : 0.05;

    const interval_inv = 1 / interval;
    const ticks = [];
    for (
      let i = Math.ceil(min * interval_inv) / interval_inv;
      i <= max;
      i += interval
    ) {
      ticks.push(i);
    }
    return ticks;
  }

  function translateLineName(value: string) {
    switch (value) {
      case "Length":
        return t("profile.length");
      case "Original Height":
        return t("profile.original_height");
      case "Smoothed Height":
        return t("profile.smoothed_height");
      default:
        return "label definition not found";
    }
  }

  useEffect(() => {
    if (profileToolValues !== undefined) {
      //deep copy the points so they can be changed without changing original values
      const jsonString = JSON.stringify(profileToolValues);
      let pointArray = JSON.parse(jsonString);
      const originalPointArray = JSON.parse(jsonString);

      let minY = 10000;
      let maxY = -10000;

      //filter peaks
      if (filterPeaks) {
        const tempArray: { x: number; y: number; z: number }[] = [];
        for (let i = 0; i < pointArray.length; i++) {
          const anteMedian =
            tempArray.length === 0
              ? pointArray[0].z
              : tempArray.reduce((acc, val, idx) => {
                  if (i - idx <= 5 && i - idx > 0) {
                    if (!acc || Math.abs(acc - val.z) < 0.5)
                      return (acc + val.z) / (acc ? 2 : 1);
                  }
                  return acc;
                }, 0);

          if (
            Math.abs(anteMedian - pointArray[i].z) < 0.5 ||
            i == pointArray.length - 1
          ) {
            const tempVector = {
              x: pointArray[i].x,
              y: pointArray[i].y,
              z: pointArray[i].z,
            };
            tempArray.push(tempVector);
          } else {
            let nextValid = 0;
            for (let j = i; j < Math.min(pointArray.length, i + 10); j++) {
              if (Math.abs(anteMedian - pointArray[j].z) < 0.5) {
                nextValid = pointArray[j].z;
                j = pointArray.length;
              }
            }

            const tempVector = {
              x: pointArray[i].x,
              y: pointArray[i].y,
              z: nextValid ? (anteMedian + nextValid) / 2 : pointArray[i].z,
            };

            tempArray.push(tempVector);
          }
        }
        pointArray = tempArray;
      }

      //smooth the curve with moving average smoothing
      pointArray = mas(pointArray);
      const storeData: Datas[] = [];
      let point_prev = [pointArray[0].x, pointArray[0].y];
      let cummulative_length = 0;
      for (let i = 0; i < pointArray.length; i++) {
        const point = [pointArray[i].x, pointArray[i].y];
        const step = Math.sqrt(
          Math.pow(point[0] - point_prev[0], 2) +
            Math.pow(point[1] - point_prev[1], 2)
        );
        point_prev = point;
        cummulative_length += step;
        storeData.push({
          Length: Number(cummulative_length.toFixed(3)),
          east: pointArray[i].x.toFixed(3),
          north: pointArray[i].y.toFixed(3),
          "Smoothed Height": pointArray[i].z.toFixed(3),
          "Original Height": originalPointArray[i].z.toFixed(3),
        });
        if (pointArray[i].z < minY) minY = pointArray[i].z;
        if (pointArray[i].z > maxY) maxY = pointArray[i].z;
      }

      // reset zoom when new profile gets drawn
      if (datas.length !== storeData.length) {
        zoomOut();
      }

      setDatas(storeData);
      const height = maxY - minY;
      if (height < 0.2) {
        minY = minY - 0.1;
        maxY = maxY + 0.1;
      }
      const length = storeData[storeData.length - 1].Length;

      const aspect_ratio_data = length / height;
      const scaled_superelevation = (1 - superelevation) ** 3; // ** 3 is used to achieve a smoother behavior when changing the superelevation
      const padding =
        (((scaled_superelevation * height) / 2) * aspect_ratio_data) /
        aspect_ratio_chart();
      maxY = maxY + padding;
      minY = minY - padding;

      setTop(maxY);
      setBottom(minY);
      setLeft(0 - length / 100); // adds padding left
      setRight(Math.round(Number(length) * 100) / 100 + length / 100); // Rounds length to 2 decimals and adds padding right
    } else {
      setDatas([defaultDatas]);
      zoomOut();
      setTop(0);
      setBottom(0);
      setLeft(0);
      setRight(0);
    }
    resetSlope();
  }, [
    profileToolValues,
    smoothLevel,
    superelevation,
    edgePreserving,
    filterPeaks,
  ]);

  const mas = (coordinates: { x: number; y: number; z: number }[]) => {
    const numCycles = 10;
    const minSlope = (18 * smoothLevel) / 100;

    //add zero vector borders
    const vectorBorder: { x: number; y: number; z: number }[] = Array(
      smoothLevel
    ).fill({ x: 0, y: 0, z: 0 });
    coordinates.unshift.apply(coordinates, vectorBorder);
    coordinates.push.apply(coordinates, vectorBorder);

    for (let cycle = 0; cycle < numCycles; cycle++) {
      for (let i = smoothLevel; i < coordinates.length - smoothLevel; i++) {
        let sum = coordinates[i].z;
        let lbool = true;
        let rbool = true;
        let left = 0;
        let right = 0;
        let used = 1;

        for (let j = 1; j <= smoothLevel; j++) {
          if (
            ((!edgePreserving &&
              Math.abs(coordinates[i + j].z - coordinates[i + j - 1].z) / 0.01 <
                5) ||
              (edgePreserving &&
                Math.abs(coordinates[i + j].z - coordinates[i + j - 1].z) /
                  0.01 <
                  minSlope)) &&
            rbool
          ) {
            right += coordinates[i + j].z;
            if (coordinates[i + j].z != 0) used++;
          } else {
            rbool = false;
          }

          if (
            ((!edgePreserving &&
              Math.abs(coordinates[i - j].z - coordinates[i - j + 1].z) / 0.01 <
                5) ||
              (edgePreserving &&
                Math.abs(coordinates[i - j].z - coordinates[i - j + 1].z) /
                  0.01 <
                  minSlope)) &&
            lbool
          ) {
            left += coordinates[i - j].z;
            if (coordinates[i - j].z != 0) used++;
          } else {
            lbool = false;
          }
        }

        sum += left + right;
        sum /= used;
        coordinates[i].z = sum;
      }
    }

    //cut zero vector borders
    return coordinates.slice(smoothLevel, coordinates.length - smoothLevel);
  };

  // download as csv profile datas
  const downloadCSVFromJson = (
    filename: string,
    arrayOfJson: { x: number; y: number; z: number }[]
  ) => {
    // convert JSON to CSV
    const replacer = (_: string, value: string) =>
      value === null ? "" : value; // specify how you want to handle null values here
    const header: ("x" | "y" | "z")[] = ["x", "y", "z"];
    const csv_arr: string[] = arrayOfJson.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(",")
    );
    csv_arr.unshift(header.join(","));
    const csv = csv_arr.join("\r\n");

    // Create link and download
    const link = document.createElement("a");
    link.setAttribute(
      "href",
      "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csv)
    );
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    changeAlertMessage({
      message: t("alert_success.profile_downloaded"),
      type: "success",
    });
  };

  //download as png profile view
  const downloadAsPNG = () => {
    if (!captureRef.current) return;
    html2canvas(captureRef.current, { scale: 2 }).then((canvas) => {
      const dataUrl = canvas.toDataURL("dataUrl/png", 1.0);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "profile.png";
      link.click();
      changeAlertMessage({
        message: t("alert_success.profile_downloaded"),
        type: "success",
      });
    });
  };

  const downloadAsDXF = async () => {
    const feature = JSON.parse(profileTool.multipointfeature.toGeoJsonString());
    const smoothProfileLine = profileToolValues || [];
    const crs = JSON.parse(profileTool.featurecollection.toGeoJsonString()).crs;
    feature.geometry.type = "LineString";
    for (let i = 0; i < smoothProfileLine.length; i++) {
      feature.geometry.coordinates[i][2] = smoothProfileLine[i].z;
    }

    const formData = new FormData();
    formData.append("format", "DXF");
    formData.append(
      "inputdata",
      JSON.stringify({
        type: "FeatureCollection",
        features: [feature],
        crs: crs,
      })
    );

    // get url for converted layer features
    try {
      const res = await fetch(`${ClientConfig.conversionURL}convert`, {
        method: "POST",
        headers: new Headers({
          ...getApiKeyHeaders(),
        }),
        body: formData,
      });
      const { data } = await res.json();
      if (data) {
        // download layer export file
        downloadFile(
          `${ClientConfig.conversionURL}${data}`,
          `profile.dxf`,
          new Headers({
            ...getApiKeyHeaders(),
          })
        )
          .then((res) => {
            if (res) {
              changeAlertMessage({
                message: t("alert_success.profile_downloaded"),
                type: "success",
              });
            }
          })
          .catch(() => {
            changeAlertMessage({
              message: t("alert_error.download_failed"),
              type: "error",
            });
          });
      }
    } catch {
      changeAlertMessage({
        message: t("alert_error.fetching_download_url_failed"),
        type: "error",
      });
    }
  };

  //zoom in based on selected area
  const zoom = () => {
    if (
      refAreaLeft === refAreaRight ||
      refAreaRight === null ||
      refAreaLeft === null
    ) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }

    setLeftZoomed(Math.min(refAreaLeft, refAreaRight));
    setRightZoomed(Math.max(refAreaLeft, refAreaRight));
    setzoomedIn(true);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  //zoomout function
  const zoomOut = () => {
    setLeftZoomed(0);
    setRightZoomed(0);
    setzoomedIn(false);
  };

  const resetSlope = () => {
    setSlopeCoordsStart(undefined);
    setSlopeCoordsEnd(undefined);
  };

  function verticalLabelLeftOrRight() {
    if (!slopeCoordsStart || !slopeCoordsEnd) return;

    const endpointRightOfStartpoint = slopeCoordsStart.x < slopeCoordsEnd.x;
    const endpointAboveStartpoint = slopeCoordsEnd.y < slopeCoordsStart.y;

    if (endpointRightOfStartpoint) {
      if (endpointAboveStartpoint) {
        return "left";
      } else {
        return "right";
      }
    } else {
      if (!endpointAboveStartpoint) {
        return "left";
      } else {
        return "right";
      }
    }
  }

  const mouseOverDatapointHandler_ = (event: any) => {
    refAreaLeft && setRefAreaRight(Number(event?.activeLabel) || null);
    const marker = event?.activeTooltipIndex;
    if (
      marker &&
      profileTool.multipointfeature.geometry.coordinates.length != 0
    ) {
      profileTool.multipointfeature.highlightPointOn(marker);
      profileTool.scene.infrascene.render();
    } else {
      profileTool.multipointfeature.highlightPointOff();
      profileTool.scene.infrascene.render();
    }
  };

  const mouseUpDatapointHandler_ = (event: any) => {
    if (
      !event ||
      !event.activePayload ||
      !event.activePayload[0] ||
      !maximizeTable ||
      hiddenLines.includes("Smoothed Height") ||
      refAreaRight
    )
      return;
    const length = parseFloat(event.activePayload[0].value);
    const height = parseFloat(
      event.activePayload[event.activePayload.length === 2 ? 1 : 2].value // take second instead of third line height in case original height is hidden
    );
    // set first point
    if (!slopeCoordsStart) {
      setSlopeCoordsStart({ x: length, y: height });
      return;
    }
    // reset slope measure
    if (slopeCoordsStart && slopeCoordsEnd) {
      resetSlope();
      return;
    }
    // set last point
    setSlopeCoordsEnd({ x: length, y: height });
    setSlopeAngle(
      calculateSlope(
        { x: slopeCoordsStart.x, y: slopeCoordsStart.y },
        { x: length, y: height }
      )
    );
    let vertical: Array<Coordinates2d> = [];
    let horizontal: Array<Coordinates2d> = [];
    const startPoint = slopeCoordsStart;
    const endPoint = { x: length, y: height };

    const endpointRightOfStartpoint = endPoint.x > startPoint.x;
    const endpointAboveStartpoint = endPoint.y > startPoint.y;

    if (endpointRightOfStartpoint) {
      // if endpoint is right of the startpoint
      vertical = [
        { x: endPoint.x, y: endPoint.y },
        { x: endPoint.x, y: startPoint.y },
      ];
      horizontal = [
        { x: startPoint.x, y: startPoint.y },
        { x: endPoint.x, y: startPoint.y },
      ];
      if (!endpointAboveStartpoint) {
        // if endpoint is below the startpoint
        vertical = [
          { x: startPoint.x, y: startPoint.y },
          { x: startPoint.x, y: endPoint.y },
        ];
        horizontal = [
          { x: startPoint.x, y: endPoint.y },
          { x: endPoint.x, y: endPoint.y },
        ];
      }
    } else {
      // if endpoint is left of the startpoint
      vertical = [
        { x: startPoint.x, y: startPoint.y },
        { x: startPoint.x, y: endPoint.y },
      ];
      horizontal = [
        { x: endPoint.x, y: endPoint.y },
        { x: startPoint.x, y: endPoint.y },
      ];
      if (endpointAboveStartpoint) {
        // if endpoint is above the startpoint
        vertical = [
          { x: endPoint.x, y: endPoint.y },
          { x: endPoint.x, y: startPoint.y },
        ];
        horizontal = [
          { x: startPoint.x, y: startPoint.y },
          { x: endPoint.x, y: startPoint.y },
        ];
      }
    }
    setSlopeCoordsVertical(vertical);
    setSlopeCoordsHorizontal(horizontal);
  };

  return (
    <Box
      width="100%"
      display={"flex"}
      alignItems={"start"}
      flexDirection={"column"}
      position={"relative"}
      gap={2}
      sx={{ fontSize: "14px" }}
    >
      {/* Empty state */}
      {!profileToolValues && (
        <Box
          width={"100%"}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
        >
          <img
            width="280px"
            alt="Designed by pch.vector / Freepik"
            src={profileToolImage}
          />
          <Typography
            fontSize={14}
            sx={{ color: darkTheme ? "white" : "darkblue.main" }}
          >
            {t("measurement.profile_empty_message")}
          </Typography>
        </Box>
      )}

      {/* maximize table button */}
      {!maximizeTable && profileToolValues && (
        <Tooltip
          title={t("tooltip.show_additional_functions")}
          placement="right"
          enterDelay={400}
          classes={{
            tooltip: "infra3d-buttonDescription",
          }}
        >
          <IconButton
            className="zoominout-button"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() =>
              monitor(apm, "maximize-profile-tool | Profile", () => {
                changeShowDescription(true);
                changeMaximizeTable(true);
              })
            }
            sx={{
              position: "absolute",
              right: 0,
              borderRadius: "0",
              zIndex: 5,
              padding: "4px",
              color: "darkblue.main",
              backgroundColor: "grey.light",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          >
            <ZoomOutMap />
          </IconButton>
        </Tooltip>
      )}
      {/* chart */}
      {profileToolValues && (
        <Box ref={captureRef} width={"100%"} sx={{ backgroundColor: "white" }}>
          <ResponsiveContainer width="100%" aspect={aspect_ratio_chart()}>
            <LineChart
              id="infra3d-chart"
              data={datas}
              style={{
                userSelect: "none",
              }}
              onMouseDown={(event) => {
                if (!maximizeTable) return;
                setRefAreaLeft(Number(event?.activeLabel) || null);
              }}
              onMouseLeave={() => {
                profileTool.multipointfeature.highlightPointOff();
                profileTool.scene.infrascene.render();
              }}
              onMouseMove={mouseOverDatapointHandler_}
              onMouseUp={(event) => {
                monitor(apm, "zoom-in | Profile", zoom);
                mouseUpDatapointHandler_(event);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              <Line
                dot={false}
                activeDot={false}
                type="monotone"
                dataKey={"Length"}
                stroke={"#000"}
                legendType={"none"}
                hide={hiddenLines.includes("Length")}
                animationDuration={0}
              />
              <Line
                dot={false}
                type="monotone"
                activeDot={false}
                dataKey={"Original Height"}
                stroke={"#FF6700"}
                hide={hiddenLines.includes("Original Height")}
                animationDuration={0}
              />
              <Line
                dot={false}
                activeDot={{ fill: "#0d92D2", r: 4 }}
                type="monotone"
                dataKey={"Smoothed Height"}
                stroke={"#0d92D2"}
                strokeWidth={"2px"}
                hide={hiddenLines.includes("Smoothed Height")}
                animationDuration={0}
              />

              {slopeCoordsStart && maximizeTable && (
                <>
                  <ReferenceDot
                    x={slopeCoordsStart.x}
                    y={slopeCoordsStart.y}
                    r={5}
                    fill="green"
                    stroke="none"
                  />
                  {slopeCoordsEnd && (
                    <>
                      <ReferenceDot
                        x={slopeCoordsEnd.x}
                        y={slopeCoordsEnd.y}
                        r={5}
                        fill="green"
                        stroke="none"
                      />
                      <ReferenceLine
                        segment={slopeCoordsVertical}
                        name="vertical"
                        stroke="green"
                        strokeWidth="2"
                        label={{
                          value:
                            Math.abs(
                              slopeCoordsStart.y - slopeCoordsEnd.y
                            ).toFixed(2) + " m",
                          position: verticalLabelLeftOrRight(),
                        }}
                      />
                      <ReferenceLine
                        segment={slopeCoordsHorizontal}
                        name="horizontal"
                        stroke="green"
                        strokeWidth="2"
                        label={{
                          value:
                            Math.abs(
                              slopeCoordsStart.x - slopeCoordsEnd.x
                            ).toFixed(2) + " m",
                          position: "bottom",
                        }}
                      />
                      <ReferenceLine
                        segment={[slopeCoordsStart, slopeCoordsEnd]}
                        stroke="green"
                        strokeWidth="2"
                        label={{
                          value: Math.abs(slopeAngle).toFixed(2) + " %",
                          position: "top",
                        }}
                      />
                    </>
                  )}
                </>
              )}

              <TooltipRechart
                trigger="hover"
                formatter={(value, name) => [
                  Number(value).toFixed(2),
                  translateLineName(name as string) + " [m]",
                ]}
                animationDuration={0}
                position={{ y: 4, x: 4 }}
                contentStyle={{
                  boxShadow:
                    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                  padding: "12px",
                  borderRadius: "4px", // Optional: rounded corners
                  border: "0px",
                  opacity: maximizeTable ? "100" : "0",
                }}
              />

              <Legend
                verticalAlign="top"
                formatter={(value) => translateLineName(value)}
                height={36}
                onClick={(event) => {
                  const { dataKey } = event;
                  if (!dataKey) return;
                  if (hiddenLines.includes(dataKey.toString())) {
                    setHiddenLines(
                      hiddenLines.filter((key) => key !== dataKey.toString())
                    );
                  } else {
                    setHiddenLines((prevState) => [
                      ...prevState,
                      dataKey.toString(),
                    ]);
                  }
                }}
              />

              <XAxis
                allowDataOverflow
                hide={true}
                dataKey="Length"
                type="number"
                domain={zoomedIn ? [leftZoomed, rightZoomed] : [left, right]}
              ></XAxis>
              <YAxis
                allowDataOverflow
                hide={!maximizeTable}
                ticks={generateChartTicksInterval(bottom, top)}
                tickFormatter={(value) => parseFloat(value).toFixed(2)}
                dataKey="Original Height"
                type="number"
                domain={[bottom, top]}
              ></YAxis>
              {/* Zoom indicator */}
              {refAreaLeft !== null && refAreaRight !== null && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  stroke="black"
                  strokeOpacity={0.3}
                />
              )}
              {/* Border around chart */}
              <ReferenceArea
                x1={zoomedIn ? Math.min(leftZoomed, rightZoomed) : left}
                x2={zoomedIn ? Math.max(leftZoomed, rightZoomed) : right}
                stroke="black"
                strokeWidth={0.5}
                fill="none"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* sliders and checkboxes */}
      {profileToolValues !== undefined && maximizeTable && (
        <Box
          width="100%"
          display={"flex"}
          gap={1}
          justifyContent={"space-between"}
          paddingBottom={0.5}
          sx={{
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            display={"flex"}
            gap={maximizeTable ? 2 : 1}
            width={"100%"}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              flex: "1",
              alignItems: "center",
            }}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              gap={5}
              justifyContent={"space-between"}
              width={maximizeTable ? "70%" : "auto"}
              sx={{ width: "100%" }}
            >
              <Box
                width={"50%"}
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={1}
                paddingLeft={"24px"}
              >
                <Slider
                  aria-label="Volume"
                  value={smoothLevel}
                  id={darkTheme ? "smoothLevelDark" : "smoothLevel"}
                  min={0}
                  max={10}
                  sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                  onChange={(_, value: number | number[]) =>
                    setSmoothLevel(value as number)
                  }
                />
                <Typography
                  color={darkTheme ? "white" : "darkblue.main"}
                  fontSize={"14px"}
                  fontWeight={400}
                >
                  {t("profile.curve_smoothing")}
                </Typography>
              </Box>
              <Box
                width={"50%"}
                display={"flex"}
                alignItems={"center"}
                flexDirection={"column"}
                gap={1}
                paddingRight={"24px"}
              >
                <Slider
                  aria-label="Volume"
                  value={superelevation}
                  id={darkTheme ? "smoothLevelDark" : "smoothLevel"}
                  step={0.01}
                  min={0}
                  max={1}
                  sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                  onChange={(_, value: number | number[]) =>
                    setSuperelevation(value as number)
                  }
                />
                <Typography
                  color={darkTheme ? "white" : "darkblue.main"}
                  fontSize={"14px"}
                  fontWeight={400}
                >
                  {t("profile.superelevation")}
                </Typography>
              </Box>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"column"}
              sx={{ width: "100%" }}
            >
              <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: darkTheme ? "white" : "darkblue.main",
                    },
                  }}
                  control={
                    <Checkbox
                      id="edgePreserving"
                      size="small"
                      checked={edgePreserving}
                      onChange={(evt) => setEdgePreserving(evt.target.checked)}
                      classes={{
                        root: darkTheme
                          ? "editFeatureRadioButtonAccentWhite"
                          : "editFeatureRadioButtonAccent",
                        checked: darkTheme
                          ? "editFeatureRadioButtonAccentWhite"
                          : "editFeatureRadioButtonAccent",
                      }}
                    />
                  }
                  label={t("profile.edge_preserving")}
                />
              </Box>
              <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: darkTheme ? "white" : "darkblue.main",
                    },
                  }}
                  control={
                    <Checkbox
                      id="filterPeaks"
                      size="small"
                      checked={filterPeaks}
                      onChange={(evt) => setFilterPeaks(evt.target.checked)}
                      classes={{
                        root: darkTheme
                          ? "editFeatureRadioButtonAccentWhite"
                          : "editFeatureRadioButtonAccent",
                        checked: darkTheme
                          ? "editFeatureRadioButtonAccentWhite"
                          : "editFeatureRadioButtonAccent",
                      }}
                    />
                  }
                  label={t("profile.filter_peaks")}
                />
              </Box>
            </Box>
          </Box>
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            {/* zoomOut Button */}
            <Button
              onClick={() => monitor(apm, "zoom-out | Profile", zoomOut)}
              variant="contained"
              disabled={!zoomedIn}
              endIcon={<ZoomOutIcon />}
            >
              {t("profile.zoom_out")}
            </Button>
            <Box position={"relative"}>
              {/* Export Button */}
              <Button
                onClick={() =>
                  monitor(apm, "show-download-options | Profile", () =>
                    setShowDownloadSubMenu(!showDownloadSubMenu)
                  )
                }
                sx={{ width: "100%" }}
                variant="contained"
                endIcon={<FileDownloadOutlined fontSize="small" />}
              >
                {t("profile.export")}
              </Button>
              {showDownloadSubMenu && (
                <Box
                  position={"absolute"}
                  right={0}
                  bottom={"calc(100% + 4px)"}
                  display={"flex"}
                  flexDirection={"column"}
                  sx={{
                    backgroundColor: darkTheme ? "darkblue.main" : "white",
                    boxShadow: 4,
                    borderRadius: "4px",
                    border: "1px #0000000a solid",
                  }}
                >
                  <Button
                    sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                    disabled={zoomedIn}
                    onClick={() =>
                      monitor(apm, "download-csv | Profile", () => {
                        downloadCSVFromJson(
                          `profil_csv.csv`,
                          profileToolValues
                        );
                        setShowDownloadSubMenu(false);
                      })
                    }
                  >
                    CSV
                  </Button>
                  <Button
                    sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                    disabled={zoomedIn}
                    onClick={() =>
                      monitor(apm, "download-dxf | Profile", () => {
                        downloadAsDXF();
                        setShowDownloadSubMenu(false);
                      })
                    }
                  >
                    DXF
                  </Button>
                  <Button
                    sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                    onClick={() =>
                      monitor(apm, "download-png | Profile", () => {
                        downloadAsPNG();
                        setShowDownloadSubMenu(false);
                      })
                    }
                  >
                    PNG
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
